import { fn } from '@ember/helper';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { SearchInput, Select } from 'tio-ui/components/forms';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import PslfAdminCompanyFormsTable from 'tio-employee/components/pslf/admin/company-forms-table';
import type { IntlService } from 'ember-intl';
import type { PslfForm } from 'tio-common/models/search';
import type { SearchModelWith } from 'tio-common/models/search';
import type AdminPslfFormsController from 'tio-employee/controllers/authenticated/admin/pslf/forms';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';

export interface AdminPslfFormsRouteSignature {
  Args: {
    controller: AdminPslfFormsController;
    model: SearchModelWith<PslfForm>;
  };
}

export default class AdminPslfFormsRoute extends Component<AdminPslfFormsRouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  formTypes = ['currentEmployer', 'priorEmployer'];

  statusOptions = [
    'borrower_signed',
    'employer_signed',
    'rejected',
    'awaiting_submit',
    'submit_pending',
    'submit_errored',
    'submitted',
    'cancelled',
    're_submitted',
    're_submit_pending',
    're_submit_errored',
    'expired',
  ];

  statusFilterItems = this.statusOptions.map((status) => {
    if (status === 'borrower_signed') {
      return {
        value: this.intl.t(`common.pslf.status.needs_review`),
        key: status,
      };
    }
    return {
      value: this.intl.t(`common.pslf.status.${status}`),
      key: status,
    };
  });

  get approverGroupIds() {
    return this.sessionContext.currentEmployee?.approverPslfGroupIds || [];
  }

  get groupFilterItems() {
    const groups = this.isGroupApprover
      ? this.groups.filter((group) => group.id && this.approverGroupIds.includes(group.id))
      : this.groups;

    return groups.map((group) => {
      return {
        value: group.employerLegalName,
        key: group.id,
      };
    });
  }

  formTypeFilterItems = this.formTypes.map((type) => {
    const formType = type === 'currentEmployer' ? 'current_employer' : 'prior_employer';
    return {
      value: this.intl.t(`pslf.forms.${formType}`),
      key: type,
    };
  });

  get company() {
    return this.sessionContext.currentRole?.company;
  }

  get groups() {
    return this.company?.pslfGroups || [];
  }

  get isGroupApprover() {
    return this.sessionContext.isPslfGroupApprover;
  }

  <template>
    <VStack>
      <Header data-test-page-title>{{t "pslf.forms.index_page_title"}}</Header>
      <Section>
        <:body>
          <div class="grid grid-cols-4 gap-4">
            <SearchInput
              data-test-search-input
              @placeholder="Search (Name, Email)"
              @searchString={{@controller.search}}
              @onInput={{fn (mut @controller.search)}}
              @onSubmit={{fn (mut @controller.search)}}
            />
            {{! @glint-expect-error: Frontile alpha 25 has typing issues with single selection mode }}
            <Select
              data-test-group-filter
              @isClearable={{true}}
              @placeholder="By group"
              @items={{this.groupFilterItems}}
              @selectedKey={{@controller.group}}
              @onSelectionChange={{fn (mut @controller.group)}}
            />
            {{! @glint-expect-error: Frontile alpha 25 has typing issues with single selection mode }}
            <Select
              data-test-status-filter
              @isClearable={{true}}
              @placeholder="By status"
              @items={{this.statusFilterItems}}
              @selectedKey={{@controller.status}}
              @onSelectionChange={{fn (mut @controller.status)}}
            />
            {{#unless this.isGroupApprover}}
              {{! @glint-expect-error: Frontile alpha 25 has typing issues with single selection mode }}
              <Select
                data-test-form-type-filter
                @isClearable={{true}}
                @placeholder="By form type"
                @items={{this.formTypeFilterItems}}
                @selectedKey={{@controller.formType}}
                @onSelectionChange={{fn (mut @controller.formType)}}
              />
            {{/unless}}
          </div>
        </:body>
      </Section>
      <Section class="overflow-auto">
        <:body>
          <PslfAdminCompanyFormsTable @model={{@model}} @controller={{@controller}} />
        </:body>
      </Section>
    </VStack>
  </template>
}
